import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { fillOfferLine, fillOfferTokens, usesOfferTokens } from '@/lib/utils/offer-tokens.utils';
import { COMMAND_CENTER_SERVICES as S } from '../../constants';
import type { CardGridContent } from './service-content';

/** Inclusions shown before "See all": enough to compare the two services at a glance. */
const PREVIEW_COUNT = 4;

export interface ServiceCardProps {
  content: CardGridContent;
  cta: ReactNode;
}

/**
 * A legal service (Issue Report, Seed Financing Package) as an in-app card:
 * the CMS block's own eyebrow, title, intro, inclusions and price line, with
 * the live Issue Report price filled in as on the public page.
 */
export function ServiceCard({ content, cta }: ServiceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const body = content.body ?? [];
  const items = content.cards ?? [];
  const needsOffer = usesOfferTokens(...body, content.priceLine, content.closingLine);
  const { data: offer } = useIssueReportOffer({ enabled: needsOffer });
  const shown = expanded ? items : items.slice(0, PREVIEW_COUNT);
  const priceLine = fillOfferLine(content.priceLine, offer);
  const closingLine = fillOfferLine(content.closingLine, offer);

  return (
    <article className="bg-surface-card border-border-subtle rounded-card flex h-full flex-col gap-4 border p-6">
      {content.eyebrow && (
        <p className="text-dkgreen text-xs font-semibold tracking-wide uppercase">
          {content.eyebrow}
        </p>
      )}
      {content.title && <h3 className="text-h4 text-navy">{content.title}</h3>}
      {body.map((paragraph, index) => (
        <p key={index} className="text-text-secondary text-sm leading-6">
          {fillOfferTokens(paragraph, offer)}
        </p>
      ))}
      {items.length > 0 && (
        <div className="flex flex-col gap-3">
          {content.cardsHeading && (
            <p className="text-navy text-sm font-semibold">{content.cardsHeading}</p>
          )}
          <ul className="flex flex-col gap-3">
            {shown.map((item, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check size={18} className="text-dkgreen mt-0.5 shrink-0" aria-hidden />
                <span className="flex flex-col">
                  <span className="text-navy text-sm font-medium">{item.title}</span>
                  {item.body && <span className="text-text-secondary text-sm">{item.body}</span>}
                </span>
              </li>
            ))}
          </ul>
          {items.length > PREVIEW_COUNT && (
            <Button variant="link" className="w-fit" onClick={() => setExpanded((open) => !open)}>
              {expanded ? S.SHOW_FEWER : S.SHOW_ALL(items.length)}
            </Button>
          )}
        </div>
      )}
      {priceLine && <p className="text-navy text-sm font-semibold">{priceLine}</p>}
      {closingLine && <p className="text-text-secondary text-sm">{closingLine}</p>}
      {/* Right under the card's own text, not pinned to the bottom: pinned, a
          short card's button floated far below its content whenever the other
          card grew. `flex` gives the CTA link a real box, so its padding takes
          up space instead of spilling over the text above. */}
      <div className="flex pt-2">{cta}</div>
    </article>
  );
}
